import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronRight, ShoppingCart, Clock, Bell, Truck, Shield, Package, Plus, Minus } from 'lucide-react';
import { getProductBySlug, products, ctaLabel } from '../data/products';
import { useCart } from '../context/CartContext';
import { ProductCard } from '../components/ProductCard';
import { StockBadge } from '../components/StockBadge';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = getProductBySlug(slug || '');
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'features' | 'includes' | 'specs'>('features');
  if (!product) return <Navigate to="/shop" replace data-id="element-618" />;
  const ButtonIcon = product.status === 'in-stock' ? ShoppingCart : product.status === 'pre-order' ? Clock : Bell;
  // Frequently bought together (other products in same category)
  const related = products.filter(p => p.id !== product.id && p.category === product.category).slice(0, 4);
  // Frequently bought together: pick 2-3 accessories or modules
  const fbt = product.category === 'Complete Systems' || product.category === 'Bundles' ? products.filter(p => p.category === 'Accessories').slice(0, 3) : products.filter(p => p.category === 'Complete Systems' && p.id !== product.id).slice(0, 3);
  return <div className="bg-white" data-id="element-619">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200 py-3" data-id="element-620">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500 overflow-x-auto whitespace-nowrap hide-scrollbar" data-id="element-621">
          <Link to="/" className="hover:text-yarbo-dark" data-id="element-622">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 mx-1.5" data-id="element-623" />
          <Link to="/shop" className="hover:text-yarbo-dark" data-id="element-624">
            Shop
          </Link>
          <ChevronRight className="w-3.5 h-3.5 mx-1.5" data-id="element-625" />
          <span className="text-yarbo-dark font-medium" data-id="element-626">{product.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="py-8 lg:py-12" data-id="element-627">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-628">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-629">
            {/* Gallery */}
            <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-630">
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border border-gray-200 p-8 lg:p-12 mb-4" data-id="element-631">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-632" />
              </div>
              <div className="grid grid-cols-4 gap-3" data-id="element-633">
                {[1, 2, 3, 4].map(i => <div key={i} className="aspect-square bg-gray-50 rounded-lg border border-gray-200 p-2 cursor-pointer hover:border-yarbo-dark" data-id="element-634">
                    <img src={product.image} alt="" className="w-full h-full object-contain opacity-60 hover:opacity-100" data-id="element-635" />
                  </div>)}
              </div>
            </motion.div>

            {/* Info */}
            <motion.div initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex flex-col" data-id="element-636">
              {product.requiresCore && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold px-3 py-2 rounded-lg mb-4 w-fit" data-id="element-637">
                  Module Only · Requires a compatible Yarbo Core
                </div>}

              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-638">
                {product.category}
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-639">
                {product.name}
              </h1>

              <div className="flex items-center gap-3 mb-4" data-id="element-640">
                <StockBadge status={product.status} size="md" data-id="element-641" />
              </div>

              <div className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-6" data-id="element-642">
                ${product.price.toLocaleString()}
              </div>

              <p className="text-base text-gray-600 mb-6 leading-relaxed" data-id="element-643">
                {product.shortDescription}
              </p>

              <ul className="space-y-2 mb-8" data-id="element-644">
                {product.features.slice(0, 4).map((f, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-645">
                    <Check className="w-4 h-4 text-yarbo-green mr-2 mt-0.5 flex-shrink-0" data-id="element-646" />
                    <span data-id="element-647">{f}</span>
                  </li>)}
              </ul>

              {/* Qty + Buy */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-648">
                <div className="flex items-center border-2 border-gray-200 rounded-lg w-fit" data-id="element-649">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="p-3 hover:bg-gray-50" aria-label="Decrease" data-id="element-650">
                    <Minus className="w-4 h-4" data-id="element-651" />
                  </button>
                  <span className="px-5 font-bold text-base" data-id="element-652">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="p-3 hover:bg-gray-50" aria-label="Increase" data-id="element-653">
                    <Plus className="w-4 h-4" data-id="element-654" />
                  </button>
                </div>
                <button onClick={() => addToCart(product, quantity)} className="flex-1 bg-yarbo-dark text-white font-bold py-4 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2 text-base" data-id="element-655">
                  <ButtonIcon className="w-5 h-5" data-id="element-656" /> {ctaLabel(product.status)}
                </button>
              </div>

              {/* Trust */}
              <div className="grid grid-cols-3 gap-3 py-5 border-t border-b border-gray-100 mb-6" data-id="element-657">
                <div className="flex items-start gap-2" data-id="element-658">
                  <Truck className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-659" />
                  <div className="text-xs" data-id="element-660">
                    <div className="font-bold text-yarbo-dark" data-id="element-661">
                      Ships Nationwide
                    </div>
                    <div className="text-gray-500" data-id="element-662">Across the U.S.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-id="element-663">
                  <Shield className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-664" />
                  <div className="text-xs" data-id="element-665">
                    <div className="font-bold text-yarbo-dark" data-id="element-666">
                      Secure Checkout
                    </div>
                    <div className="text-gray-500" data-id="element-667">Encrypted</div>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-id="element-668">
                  <Package className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-669" />
                  <div className="text-xs" data-id="element-670">
                    <div className="font-bold text-yarbo-dark" data-id="element-671">
                      {product.status === 'in-stock' ? 'Ready to Ship' : product.status === 'pre-order' ? 'Pre-Order' : 'Coming Soon'}
                    </div>
                    <div className="text-gray-500" data-id="element-672">
                      {product.status === 'in-stock' ? 'In stock' : 'Reserve now'}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-gray-50 py-14 border-t border-gray-200" data-id="element-673">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-674">
          <div className="flex space-x-1 bg-gray-200 p-1 rounded-xl mb-6 overflow-x-auto hide-scrollbar" data-id="element-675">
            {[{
            key: 'features',
            label: 'Features'
          }, {
            key: 'includes',
            label: "What's Included"
          }, {
            key: 'specs',
            label: 'Compatibility'
          }].map(t => <button key={t.key} onClick={() => setActiveTab(t.key as any)} className={`flex-1 py-3 px-4 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${activeTab === t.key ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600'}`} data-id="element-676">
                {t.label}
              </button>)}
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-200" data-id="element-677">
            {activeTab === 'features' && <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-678">
                {product.features.map((f, idx) => <li key={idx} className="flex items-start" data-id="element-679">
                    <Check className="w-5 h-5 text-yarbo-green mr-3 mt-0.5 flex-shrink-0" data-id="element-680" />
                    <span className="text-gray-700" data-id="element-681">{f}</span>
                  </li>)}
              </ul>}
            {activeTab === 'includes' && <div data-id="element-682">
                {product.requiresCore && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium p-4 rounded-lg mb-5" data-id="element-683">
                    This is a module-only product. The Yarbo Core is required
                    for operation and is sold separately or as part of a
                    complete system.
                  </div>}
                <ul className="space-y-2" data-id="element-684">
                  {product.includes.map((item, idx) => <li key={idx} className="flex items-center bg-gray-50 px-4 py-3 rounded-lg" data-id="element-685">
                      <Package className="w-4 h-4 text-gray-500 mr-3" data-id="element-686" />
                      <span className="font-medium text-gray-800" data-id="element-687">{item}</span>
                    </li>)}
                </ul>
              </div>}
            {activeTab === 'specs' && <div className="prose max-w-none text-gray-600" data-id="element-688">
                <p data-id="element-689">
                  Yarbo Y Series uses a modular architecture: a central Yarbo
                  Core handles navigation, battery, and drive, while modules
                  attach to perform specific tasks (mowing, snow clearing, leaf
                  blowing, trimming).
                </p>
                <p className="mt-3" data-id="element-690">
                  {product.category === 'Modules' ? 'This module attaches to a compatible Yarbo Core. The Core is not included with this product.' : product.category === 'Accessories' ? 'This accessory is compatible with Yarbo Y Series systems.' : 'This complete system includes the Yarbo Core and the modules listed under "What\'s Included."'}
                </p>
              </div>}
          </div>
        </div>
      </section>

      {/* Frequently bought together */}
      {fbt.length > 0 && <section className="py-14 border-t border-gray-200" data-id="element-691">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-692">
            <h2 className="text-2xl font-extrabold text-yarbo-dark mb-6" data-id="element-693">
              Frequently Bought Together
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-694">
              {fbt.map(p => <ProductCard key={p.id} product={p} compact data-id="element-695" />)}
            </div>
          </div>
        </section>}

      {/* Related */}
      {related.length > 0 && <section className="py-14 bg-gray-50 border-t border-gray-200" data-id="element-696">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-697">
            <h2 className="text-2xl font-extrabold text-yarbo-dark mb-6" data-id="element-698">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-699">
              {related.map(p => <ProductCard key={p.id} product={p} compact data-id="element-700" />)}
            </div>
          </div>
        </section>}

      {/* Sticky mobile Add to Cart */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)]" data-id="element-701">
        <div className="flex items-center gap-3" data-id="element-702">
          <div className="flex-1 min-w-0" data-id="element-703">
            <div className="text-sm font-bold text-yarbo-dark truncate" data-id="element-704">
              {product.name}
            </div>
            <div className="text-lg font-extrabold text-yarbo-dark" data-id="element-705">
              ${product.price.toLocaleString()}
            </div>
          </div>
          <button onClick={() => addToCart(product, quantity)} className="bg-yarbo-dark text-white font-bold px-5 py-3 rounded-lg flex items-center gap-2 whitespace-nowrap" data-id="element-706">
            <ButtonIcon className="w-4 h-4" data-id="element-707" /> {ctaLabel(product.status)}
          </button>
        </div>
      </div>
    </div>;
}